import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProject } from "../../api/use-project";
import { Box, Button, Paper, Tab, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { RenameProject } from "./common/rename-project.jsx";
import { useCallback, useContext, useEffect, useState } from "react";
import { DeleteProject } from "./common/delete-project.jsx";
import { LoadingPaper } from "../ui/loading-paper";
import { ErrorPaper } from "../ui/error-paper";
import { ProjectMedias } from "./project-medias";
import { ProjectInfo } from "./project-info";
import { ProjectProgress } from "./project-progress";
import { ProjectResult } from "./result/project-result.jsx";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { ProjectSettings } from "./common/project-settings.jsx";
import { UserContext } from "../../contexts/user-context";
import { ProjectContributors } from "./project-contributors";

export const ProjectDetails = () => {
  const params = useParams();
  const { t } = useTranslation(["project", "common"]);
  const { user } = useContext(UserContext);
  const [project, setProject] = useState(null);
  const [{ data, error, loading }, execute] = useProject(params.projectId);
  useEffect(() => {
    setProject(data);
    if (data && data.media.length === 0) {
      setTab("2");
    }
  }, [data]);

  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);
  const [renameProjectOpen, setRenameProjectOpen] = useState(false);
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);

  const handleRenameProjectClose = useCallback((name) => {
    if (name) setProject((p) => ({ ...p, name: name }));
    setRenameProjectOpen(false);
  }, []);

  const handleProjectSettingsClose = useCallback((patch) => {
    if (patch) setProject((p) => ({ ...p, ...patch }));
    setProjectSettingsOpen(false);
  }, []);

  const [tab, setTab] = useState("1");

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  const isOwnProject = user && project?.createdById === user.id;

  if (loading) return <LoadingPaper/>;

  if (error)
    return (
      <ErrorPaper
        message={
          error.response.status === 404
            ? t("notFoundError", { object: t("project") })
            : t("genericError", { action: t("loadProject") })
        }
      />
    );

  return (
    project && (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex" }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            {project?.name}
          </Typography>
          {user && (user.role === "admin" || user.role === "developer") && (
            <>
              <Button
                startIcon={<SettingsIcon/>}
                onClick={() => setProjectSettingsOpen(true)}
              >
                {t("settings")}
              </Button>
              <ProjectSettings
                open={projectSettingsOpen}
                project={project}
                onClose={handleProjectSettingsClose}
              />
            </>
          )}
          {isOwnProject && (
            <>
              <Button
                startIcon={<EditIcon/>}
                onClick={() => setRenameProjectOpen(true)}
              >
                {t("rename")}
              </Button>
              <RenameProject
                open={renameProjectOpen}
                project={project}
                onClose={handleRenameProjectClose}
              />
              <Button
                startIcon={<DeleteIcon/>}
                color="error"
                onClick={() => setDeleteProjectOpen(true)}
              >
                {t("delete")}
              </Button>
              <DeleteProject
                open={deleteProjectOpen}
                project={project}
                onClose={() => setDeleteProjectOpen(false)}
              />
            </>
          )}
        </Box>
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChange} aria-label="" variant="scrollable">
              <Tab label={t("state")} value="1" disabled={project.media.length === 0}/>
              <Tab label={t("media")} value="2"/>
              <Tab label={t("result")} value="3" disabled={project.media.length === 0}/>
              <Tab label={t("details")} value="4" disabled={project.media.length === 0}/>
              {isOwnProject && <Tab label={t("contributors")} value="5" disabled={project.media.length === 0}/>}
            </TabList>
          </Box>
          <TabPanel value="1">
            <ProjectProgress project={project} reloadProject={execute}/>
          </TabPanel>
          <TabPanel value="2">
            <ProjectMedias project={project} onChanged={execute}/>
          </TabPanel>
          <TabPanel value="3">
            <ProjectResult project={project} onChanged={execute}/>
          </TabPanel>
          <TabPanel value="4">
            <ProjectInfo project={project}/>
          </TabPanel>
          <TabPanel value="5">
            <ProjectContributors project={project} reloadProject={execute}/>
          </TabPanel>
        </TabContext>
      </Paper>
    )
  );
};
